/**
 * MeetingMaterialDialog: 화면공유 > 문서공유 > 회의자료 클릭 시 노출되는 회의자료 전용 팝업.
 * 설정 다이얼로그와 달리 좌측 메뉴 없이 회의자료 목록만 표시.
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import MeetingMaterialTab from './SettingsDialog/tabs/MeetingMaterialTab';
import type { MaterialListItem } from '@/api';
import { handleMaterialDocumentShare, getIsOpenWhiteboardSession } from '@/utils/whiteboard';
import LUXDialog from '@/components/Dialog';

interface MeetingMaterialDialogProps {
  onClose: () => void;
}

const MeetingMaterialDialog: React.FC<MeetingMaterialDialogProps> = ({ onClose }) => {
  const [actionBtnText, setActionBtnText] = useState('문서 공유');
  const [actionBtnDisabled, setActionBtnDisabled] = useState(true);
  const actionBtnClickRef = useRef<(() => void) | null>(null);

  const handleStartDocumentShare = useCallback(
    async (material: MaterialListItem) => {
      if (!material.fileInfo) return;

      if (getIsOpenWhiteboardSession()) {
        LUXDialog.alert('이미 문서공유 또는 스케치가 진행중입니다.', {
          type: 'warning',
          useRenewalStyle: true,
        });
        return;
      }

      onClose();

      try {
        await handleMaterialDocumentShare({
          fileName: material.fileName,
          newFileName: material.fileInfo.newFileName,
          bucketType: material.fileInfo.bucketType,
        });
      } catch (err) {
        console.error('[MeetingMaterialDialog] 문서공유 시작 실패:', err);
        LUXDialog.alert('문서 공유에 실패했습니다. 지원하지 않는 파일 형식이거나\n용량이 초과되었을 수 있습니다.', {
          type: 'warning',
          useRenewalStyle: true,
        });
      }
    },
    [onClose],
  );

  const handleActionBtnClick = useCallback(() => {
    actionBtnClickRef.current?.();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const handleDimmedClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  const dialog = (
    <div className="rtc rtc_new3 LUX_basic_dialog notosans_font" style={{ zIndex: 1502 }}>
      <div className="dimmed" onClick={handleDimmedClick} />
      <div className="dialog_wrap_tbl">
        <div className="dialog_wrap_tblcel">
          <div className="dialog_wrap" style={{ border: 0, borderRadius: '20px' }}>
            <div
              className="dialog_content rtc_dialog rtc_new4_dialog rtc_new5_dialog roundstyle"
              style={{ width: '740px' }}
            >
              <div className="dialog_data">
                <div className="dialog_data_tit">
                  <h1 className="txtcnt">회의자료</h1>
                  <button type="button" className="LUX_basic_btn btn_clr" onClick={onClose}>
                    <span className="sp_lux">닫기</span>
                  </button>
                </div>
                <div className="dialog_data_area rtcDialogSetup_dataArea">
                  <div className="rtcDialogSetupNew">
                    <div className="rtcDialogSetupNew__right">
                      <MeetingMaterialTab
                        setActionBtnText={setActionBtnText}
                        setActionBtnDisabled={setActionBtnDisabled}
                        onActionBtnClick={handleStartDocumentShare}
                        actionBtnClickRef={actionBtnClickRef}
                        documentShareMode
                      />
                    </div>
                  </div>
                  <div className="dialog_btnbx">
                    <div>
                      <button
                        type="button"
                        className={`common_round_btn rtcNew_btnType blue${actionBtnDisabled ? ' disabled' : ''}`}
                        disabled={actionBtnDisabled}
                        onClick={handleActionBtnClick}
                      >
                        {actionBtnText && actionBtnText !== '문서 공유' ? (
                          <span className="rtc_new5_dialog__footerBtn">
                            <span className="name ellipsis">
                              {actionBtnText.lastIndexOf('.') > 0
                                ? actionBtnText.substring(0, actionBtnText.lastIndexOf('.'))
                                : actionBtnText}
                            </span>
                            {actionBtnText.lastIndexOf('.') > 0 && (
                              <span className="ext">
                                {actionBtnText.substring(actionBtnText.lastIndexOf('.'))}
                              </span>
                            )}
                            <span className="endText">문서 공유</span>
                          </span>
                        ) : (
                          '문서 공유'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(dialog, document.body);
};

export default MeetingMaterialDialog;
